import { EntitySelection } from "models/ats-organization";
import { Range } from "views/components/date-range";

export enum MarketingOutreachSentStatus {
    InQueue = "in-queue",
    Sending = "sending",
    Sent = "sent",
    Error = "error"
}

export enum MarketingOutreachSentStatusIndex {
    InQueue = 0,
    Sending = 1,
    Sent = 2,
    Error = 3
}

export enum MarketingOutreachSentStatusName {
    "in-queue",
    "sending",
    "sent",
    "error"
}

export class MarketingOutreachSentStatusCounts {
    "in-queue"?: number;
    "sending"?: number;
    "sent"?: number;
    "error"?: number;
}

export enum MarketingOutreachMediaTypes {
    email = "email",
    print = "print",
    text = "text",
    voice = "voice"
}

export enum MarketingOutreachSentSource {
    Application = "Application",
    Contact = "Contact",
    Quick = "Quick",
    Campaign = "Campaign",
    Event = "Event",
    System = "System"
}

export class MarketingOutreachSentStats {
    sent?: number;
    error?: number;
    invalid_emails?: number;
    item_already_received?: number;
    opted_out_emails?: number;
    invalid_phones?: number;
    opted_out_phones?: number;
}

export enum VoiceMessageType {
    recorded = "recorded",
    typed = "typed"
}

export enum EventMessageTypes {
    invitation = "invitation",
    reminder = "reminder",
    followUp = "follow-up",
    weMissedYou = "we-missed-you",
    waitlisted = "waitlisted"
}

export enum EventFrequencies {
    oneOccurrence = "one-occurrence",
    futureOccurrences = "future-occurrences"
}

export class MarketingOutreachCampaign {
    Id: number = null;
    AtsOrgId: string = null;
    Archived: number = null;
    BulkMessage: number = null;
    DirectMessage: number = null;
    ConversationMessage: number = null;
    EventSeriesId: string = null;
    EventOccurrenceId: string = null;
    EventMessageType: EventMessageTypes = null;
    EventFrequency: EventFrequencies = null;
    Name: string = null;
    Description: string = null;
    EndDate: string = null;
    StartDate: string = null;
    RunAs: string = null;
    Filter: string = null;
}

export class MarketingOutreachSentFailedDelivery {
    Id: number = null;
    OutreachSentId: number = null;
    ArchiveId: string = null;
    Code: string = null;
    Reason: string = null;
    MailgunResponse: string = null;
    AtsRecordType: "applications" | "contacts" | "users" | "external" = null;
    AtsRecordId: string = null;
    CreatedAt: Date = null;
    UpdatedAt: Date = null;
}

export class MarketingOutreachSentBase {
    AtsOrgId: string = null;
    AtsOrgName?: string = null;
    AtsOrgTimezone?: string = null;
    AtsTemplateId: string = null;
    AtsUserId: string = null;
    ClickRating: number = 0;
    DeliveryAddress: string = null;
    DeliveryType: "applications" | "contacts" | "users" = null;
    Direct: boolean = false;
    EntitySelection: EntitySelection = null;
    Filter: string = null;
    FilterType: "filter" | "ids" = null;
    FromAddress: string = null;
    FromName: string = null;
    HtmlFile: string = null;
    Id: string = null;
    MediaType: MarketingOutreachMediaTypes = null;
    OpenRating: number = 0;
    OutreachId: number = null;
    PrintTemplateId: number = null;
    RecordType: "applications" | "contacts" | "users" | "external" = null;
    ReplyRating: number = 0;
    ReplyToAddress: string = null;
    ReplyToName: string = null;
    RunAs: string = null;
    Schedule: Date = null;
    SentAt: Date = null;
    SentRating: number = 0;
    Source: MarketingOutreachSentSource = null;
    SourceType: string = null;
    Stats: MarketingOutreachSentStats = null;
    Status: MarketingOutreachSentStatus = null;
    Subject: string = null;
    Test: boolean = false;
    TextFile: string = null;
    Transactional: boolean = false;
    VoiceMessageType: VoiceMessageType = null;
    VoiceMessageUrl: string = null;
    VoiceMessageVoice: string = null;
    campaign?: MarketingOutreachCampaign = null;
    failedDeliveries?: MarketingOutreachSentFailedDelivery[] = [];
}

export class MarketingOutreachSentResult {
    list: MarketingOutreachSentBase[];
    total: number;
}

export class MarketingOutreachArchive {
    Id: number = null;
    OutreachId: number = null;
    OutreachSentId: number = null;
    AtsRecordType: "applications" | "contacts" | "users" = null;
    AtsRecordId: string = null;
    Test: number = 0; // Email, Text Message, Voice Message
    PhoneNumber: number = null; // Text Message, Voice Message
    Status: "" | "success" | "no answer" = null; // Voice Message
    ArchiveId: string = null; // Email
    EmailAddress: string = null; // Email
    Open: number = 0; // Email
    ProcessedAt: Date = null; // Print Piece
    MailedAt: Date = null; // Print Piece
    Deleted: number = 0; // Print Piece
}

export type OrganizationSelection = {
    Id: string;
    Name: string;
};

export type Filter = {
    organization?: OrganizationSelection;
    outreachSentSource?: MarketingOutreachSentSource;
    status?: number;
    range?: string | Range;
};

export type OrganizationFilter = {
    type?: number | null;
    tags?: string[] | null;
};

export type OrganizationIdData = {
    id: string;
    name: string;
    timezone: string;
};

export type SelectionItem = {
    label: string;
    value: number | string;
    color?: string;
    hexColor?: string;
};

export type StatusAggregationItem = {
    Key: number;
    Total: number;
};

export type HeadersType = {
    label: string;
    sort?: string;
    key?: string;
    class?: string;
};

export type SortParamsType = {
    column: string;
    order: "DESC" | "ASC";
};

export type Flag = {
    open: boolean;
};

export const OUTREACH_STATUSES: SelectionItem[] = [
    { label: "All Statuses", value: null },
    { label: "In Queue", value: MarketingOutreachSentStatusIndex.InQueue, color: "pending-color", hexColor: "#fde68a" },
    { label: "Sending", value: MarketingOutreachSentStatusIndex.Sending, color: "pending-color", hexColor: "#fde68a" },
    { label: "Sent", value: MarketingOutreachSentStatusIndex.Sent, color: "complete-color", hexColor: "#a7f3d0" },
    { label: "Error", value: MarketingOutreachSentStatusIndex.Error, color: "error-color", hexColor: "#fecaca" }
];

export const OUTREACH_SENT_SOURCE_FILTER: SelectionItem[] = [
    { label: "Any source", value: null },
    { label: "Application", value: MarketingOutreachSentSource.Application },
    { label: "Contact", value: MarketingOutreachSentSource.Contact },
    { label: "Quick message", value: MarketingOutreachSentSource.Quick },
    { label: "Campaign", value: MarketingOutreachSentSource.Campaign },
    { label: "Event", value: MarketingOutreachSentSource.Event },
    { label: "System", value: MarketingOutreachSentSource.System }
];
